/**
 * Los MODOS del chat del asistente (CQ4b · PLAN §3): hoy solo «Cuaderno de
 * campo». Se entra escribiendo «@» en la caja (al principio o tras un espacio),
 * desde el «+» —la vía principal en el móvil, donde la «@» está en el segundo
 * teclado— o desde la sugerencia del cuaderno en Registros. El modo elegido se
 * ve como un chip con su × y viaja en cada turno como `body.mode`; la edge lo
 * filtra con una lista blanca (`supabase/functions/ipm-ai-assistant/mode.ts`) y
 * mira el interruptor de la empresa (`useCuadernoCampoEnabled`).
 *
 * PURO (sin React): lo prueban assistantModeCuaderno.render.test.ts y
 * assistantModes.test.ts.
 */

export type AssistantModeId = "cuaderno";

export interface AssistantModeDef {
  id: AssistantModeId;
  /** Clave i18n del nombre («Cuaderno de campo»). */
  labelKey: string;
  /** Clave i18n de la línea de ayuda del menú. */
  hintKey: string;
  /** Clave i18n del placeholder de la caja mientras el modo está puesto. */
  placeholderKey: string;
  /** Clave i18n del primer mensaje cuando se entra desde una sugerencia. */
  openerKey: string;
  /** Palabras con las que se filtra el menú al teclear tras la «@» (en minúsculas, sin tildes). */
  keywords: string[];
}

export const ASSISTANT_MODES: AssistantModeDef[] = [
  {
    id: "cuaderno",
    labelKey: "ipmAssistant.mode.cuaderno",
    hintKey: "ipmAssistant.mode.cuadernoHint",
    placeholderKey: "ipmAssistant.mode.cuadernoPlaceholder",
    openerKey: "ipmAssistant.mode.cuadernoOpener",
    keywords: ["cuaderno", "campo", "field", "notebook", "cahier", "caderno", "quaderno", "junta"],
  },
];

export const modeById = (id: string | null | undefined): AssistantModeDef | undefined => ASSISTANT_MODES.find((m) => m.id === id);

/** Minúsculas y sin tildes, para filtrar por lo tecleado tras la «@». */
const plegar = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * ¿Hay una «@» ABIERTA justo antes del cursor? Cuenta solo si va al principio
 * de la caja o tras un espacio o salto de línea (un correo «demo@gmail.com» no
 * abre nada) y lo tecleado después de ella no lleva espacios. Devuelve dónde
 * empieza la «@» y lo que se lleva escrito detrás (para filtrar el menú).
 */
export function atTokenAt(text: string, caret: number): { start: number; query: string } | null {
  const hasta = Math.max(0, Math.min(caret, text.length));
  const antes = text.slice(0, hasta);
  const at = antes.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(antes[at - 1])) return null;
  const query = antes.slice(at + 1);
  if (/\s/.test(query) || query.length > 24) return null;
  return { start: at, query };
}

/** Los modos que casan con lo tecleado tras la «@» (todos si no hay nada). */
export function filterModes(modes: AssistantModeDef[], query: string, label: (m: AssistantModeDef) => string): AssistantModeDef[] {
  const q = plegar(query.trim());
  if (!q) return modes;
  return modes.filter((m) => plegar(label(m)).includes(q) || m.keywords.some((k) => k.startsWith(q) || q.startsWith(k)));
}

/** La caja sin la «@» y lo tecleado tras ella (lo que abrió el menú). */
export function stripAtToken(text: string, token: { start: number; query: string }): string {
  const fin = token.start + 1 + token.query.length;
  const antes = text.slice(0, token.start);
  const despues = text.slice(fin);
  return `${antes}${despues}`.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
}
